import { parseInventoryQuantity } from '../../../../../utils/inventoryInputs';
import React, { useState } from 'react';
import { SortableContainer, SortableElement, SortableHandle } from 'react-sortable-hoc';
import BulkCreateDescriptionModal from './BulkCreateDescriptionModal';
import CategoryBadgePicker from './CategoryBadgePicker';
import CategoryRowPopover from './CategoryRowPopover';
import { selectInputOnFocus } from './bulkCreateExcelUtils';
import {
  getDescriptionFirstLine,
  getManualFieldDisplay,
  getPriceAmountField,
  isBulkCreateRowValid,
  setDescriptionFirstLine
} from './bulkCreateManualUtils';
import { getPriceCurrencyOption, PRICE_CURRENCY_UAH } from './constants';

const EDITABLE_COLUMNS = [
  { field: 'productCode', placeholder: 'Артикул', width: '130px', mono: true },
  { field: 'barcode', placeholder: 'Штрихкод', width: '140px', mono: true },
  { field: 'manufacturerUrl', placeholder: 'Посилання', width: '260px' },
  { field: 'quantity', width: '72px' }
];

const DragHandle = SortableHandle(() => (
  <td className="align-middle text-center text-muted bulk-create-drag-handle" style={{ width: '28px', cursor: 'grab', touchAction: 'none' }}>
    <i className="bi bi-grip-vertical" />
  </td>
));

const EditableCell = ({
  value,
  field,
  rowIndex,
  loading,
  placeholder,
  mono,
  onChangeField,
  onPasteField,
  width
}) => (
  <td className="align-middle p-1" style={width ? { width, minWidth: width } : undefined}>
    <input
      type="text"
      className={`form-control form-control-sm${mono ? ' text-monospace' : ''}${field === 'quantity' && value !== '' && parseInventoryQuantity(value, { allowZero: true }) == null ? ' border-danger' : ''}`}
      title={field === 'quantity' ? 'Кількість має бути цілим невід’ємним числом' : undefined}
      value={value}
      placeholder={placeholder || ''}
      disabled={loading}
      onFocus={selectInputOnFocus}
      onChange={(e) => onChangeField(rowIndex, field, e.target.value)}
      onPaste={(e) => onPasteField(e, rowIndex, field)}
      style={{ minWidth: width || '72px' }}
    />
  </td>
);

const DescriptionPreviewCell = ({
  value,
  rowIndex,
  loading,
  onOpenEditor,
  onChangeField,
  onPasteField
}) => {
  const preview = getDescriptionFirstLine(value);
  const hasMoreLines = String(value || '').replace(/\r\n/g, '\n').includes('\n');
  return (
    <td className="align-middle p-1" style={{ width: '220px', minWidth: '200px' }}>
      <div className="position-relative">
        <input
          type="text"
          className="form-control form-control-sm"
          value={preview}
          placeholder="Опис"
          disabled={loading}
          title={hasMoreLines ? 'Є ще рядки — відкрийте повний опис' : (value || '')}
          onFocus={selectInputOnFocus}
          onChange={(e) => onChangeField(rowIndex, 'description', setDescriptionFirstLine(value, e.target.value))}
          onPaste={(e) => onPasteField(e, rowIndex, 'description')}
          style={{ paddingRight: '1.75rem', minWidth: '200px' }}
        />
        <i
          className="bi bi-arrows-fullscreen text-muted"
          role="button"
          tabIndex={loading ? -1 : 0}
          title="Відкрити повний опис"
          aria-label="Відкрити повний опис"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (!loading) onOpenEditor(rowIndex);
          }}
          onKeyDown={(e) => {
            if (loading) return;
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onOpenEditor(rowIndex);
            }
          }}
          style={{
            position: 'absolute',
            right: '8px',
            top: '50%',
            transform: 'translateY(-50%)',
            fontSize: '0.75rem',
            cursor: loading ? 'not-allowed' : 'pointer',
            opacity: loading ? 0.4 : (hasMoreLines ? 1 : 0.75),
            lineHeight: 1
          }}
        />
      </div>
    </td>
  );
};

const DiscountCells = ({
  item,
  rowIndex,
  loading,
  discountCategories,
  onChangeDiscount
}) => (
  <>
    <td className="align-middle p-1" style={{ width: '140px', minWidth: '140px' }}>
      <select
        className="form-control form-control-sm"
        value={item.discountCategoryName || ''}
        disabled={loading}
        onChange={(e) => onChangeDiscount(rowIndex, 'discountCategoryName', e.target.value)}
        title={item.discountCategoryName || 'Категорія знижки'}
      >
        <option value="">—</option>
        {discountCategories.map(name => (
          <option key={name} value={name}>{name}</option>
        ))}
      </select>
    </td>
    <td className="align-middle p-1" style={{ width: '72px', minWidth: '72px' }}>
      <input
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        className="form-control form-control-sm text-center"
        value={item.discount == null || item.discount === '' ? '0' : String(item.discount)}
        disabled={loading}
        onFocus={selectInputOnFocus}
        onChange={(e) => onChangeDiscount(rowIndex, 'discount', e.target.value)}
        onBlur={() => {
          if (item.discount === '' || item.discount == null) {
            onChangeDiscount(rowIndex, 'discount', '0');
          }
        }}
      />
    </td>
  </>
);

const SortableManualRow = SortableElement(({
  item,
  rowIndex,
  loading,
  categoryMode,
  priceCurrency,
  discountCategories,
  expandedCategoryRow,
  setExpandedCategoryRow,
  toggleItemSelected,
  toggleItemCategory,
  rowCategories,
  onChangeField,
  onPasteField,
  onChangeDiscount,
  onRemoveRow,
  onOpenDescriptionEditor
}) => {
  const rowValid = isBulkCreateRowValid(item);
  const currencyOpt = getPriceCurrencyOption(priceCurrency);
  const priceField = getPriceAmountField(priceCurrency);
  const showUahPreview = priceCurrency !== PRICE_CURRENCY_UAH;

  return (
    <tr
      style={item.selected === false ? { opacity: 0.45 } : undefined}
      className={item.selected === false ? 'text-muted' : undefined}
    >
      <td className="align-middle" onMouseDown={(e) => e.stopPropagation()}>
        <input
          type="checkbox"
          checked={item.selected !== false}
          onChange={() => toggleItemSelected(rowIndex)}
          disabled={loading}
          title={rowValid ? undefined : 'Для створення потрібні назва, артикул та коректна ціла кількість'}
          style={{ width: '16px', height: '16px', cursor: 'pointer' }}
        />
      </td>
      <DragHandle />
      <EditableCell
        value={getManualFieldDisplay(item, 'name')}
        field="name"
        rowIndex={rowIndex}
        loading={loading}
        placeholder="Назва"
        onChangeField={onChangeField}
        onPasteField={onPasteField}
        width="280px"
      />
      <DescriptionPreviewCell
        value={item.description}
        rowIndex={rowIndex}
        loading={loading}
        onOpenEditor={onOpenDescriptionEditor}
        onChangeField={onChangeField}
        onPasteField={onPasteField}
      />
      {EDITABLE_COLUMNS.map(col => (
        <EditableCell
          key={col.field}
          value={getManualFieldDisplay(item, col.field)}
          field={col.field}
          rowIndex={rowIndex}
          loading={loading}
          placeholder={col.placeholder}
          mono={col.mono}
          onChangeField={onChangeField}
          onPasteField={onPasteField}
          width={col.width}
        />
      ))}
      <EditableCell
        value={getManualFieldDisplay(item, priceField)}
        field="priceAmount"
        rowIndex={rowIndex}
        loading={loading}
        placeholder={currencyOpt.label}
        onChangeField={onChangeField}
        onPasteField={onPasteField}
        width="88px"
      />
      {showUahPreview ? (
        <td className="small align-middle text-muted" style={{ width: '80px', minWidth: '80px' }} title="грн (за курсом)">
          {item.price || '—'}
        </td>
      ) : null}
      <DiscountCells
        item={item}
        rowIndex={rowIndex}
        loading={loading}
        discountCategories={discountCategories}
        onChangeDiscount={onChangeDiscount}
      />
      {categoryMode === 'each' ? (
        <td className="align-middle">
          <CategoryRowPopover
            open={expandedCategoryRow === rowIndex}
            onClose={() => setExpandedCategoryRow(null)}
            trigger={(
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm py-0"
                onClick={() => setExpandedCategoryRow(expandedCategoryRow === rowIndex ? null : rowIndex)}
                disabled={loading}
              >
                {(item.categoryIds || []).length ? `${item.categoryIds.length} кат.` : 'Обрати'}
              </button>
            )}
          >
            <CategoryBadgePicker
              compact
              categories={rowCategories}
              selectedIds={item.categoryIds || []}
              onToggle={(catId) => toggleItemCategory(rowIndex, catId)}
            />
          </CategoryRowPopover>
        </td>
      ) : null}
      <td className="align-middle text-center" style={{ width: '36px' }}>
        <button
          type="button"
          className="btn btn-link btn-sm text-danger p-0"
          title="Видалити рядок"
          onClick={() => onRemoveRow(rowIndex)}
          disabled={loading}
        >
          <i className="bi bi-x-lg" />
        </button>
      </td>
    </tr>
  );
});

const SortableManualTableBody = SortableContainer(({
  items,
  loading,
  categoryMode,
  priceCurrency,
  discountCategories,
  expandedCategoryRow,
  setExpandedCategoryRow,
  toggleItemSelected,
  toggleItemCategory,
  rowCategories,
  onChangeField,
  onPasteField,
  onChangeDiscount,
  onRemoveRow,
  onOpenDescriptionEditor
}) => (
  <tbody>
    {items.map((item, index) => (
      <SortableManualRow
        key={item.key}
        index={index}
        item={item}
        rowIndex={index}
        loading={loading}
        categoryMode={categoryMode}
        priceCurrency={priceCurrency}
        discountCategories={discountCategories}
        expandedCategoryRow={expandedCategoryRow}
        setExpandedCategoryRow={setExpandedCategoryRow}
        toggleItemSelected={toggleItemSelected}
        toggleItemCategory={toggleItemCategory}
        rowCategories={rowCategories}
        onChangeField={onChangeField}
        onPasteField={onPasteField}
        onChangeDiscount={onChangeDiscount}
        onRemoveRow={onRemoveRow}
        onOpenDescriptionEditor={onOpenDescriptionEditor}
      />
    ))}
  </tbody>
));

const BulkCreateManualTable = ({
  items,
  categoryMode,
  loading,
  priceCurrency,
  discountCategories,
  expandedCategoryRow,
  setExpandedCategoryRow,
  toggleItemSelected,
  toggleItemCategory,
  rowCategories,
  onSortEnd,
  onChangeField,
  onPasteField,
  onChangeDiscount,
  onRemoveRow
}) => {
  const [descriptionEditKey, setDescriptionEditKey] = useState(null);
  const currencyOpt = getPriceCurrencyOption(priceCurrency);
  const showUahPreview = priceCurrency !== PRICE_CURRENCY_UAH;
  const colSpan = (categoryMode === 'each' ? 14 : 13) + (showUahPreview ? 1 : 0);
  const descriptionEditIndex = descriptionEditKey == null
    ? -1
    : items.findIndex(it => it.key === descriptionEditKey);
  const editingItem = descriptionEditIndex >= 0 ? items[descriptionEditIndex] : null;

  const openDescriptionEditor = (rowIndex) => {
    const item = items[rowIndex];
    if (item) setDescriptionEditKey(item.key);
  };

  const closeDescriptionEditor = () => setDescriptionEditKey(null);

  return (
    <>
      <style>{`
        .bulk-create-manual-table tbody tr {
          user-select: none;
          -webkit-user-select: none;
        }
        .bulk-create-manual-table tbody input[type="checkbox"],
        .bulk-create-manual-table tbody input.form-control,
        .bulk-create-manual-table tbody select.form-control {
          user-select: auto;
          -webkit-user-select: auto;
        }
        .bulk-create-sortable-helper {
          display: table;
          background: #fff;
          box-shadow: 0 6px 16px rgba(0, 0, 0, 0.15);
          z-index: 1060;
          user-select: none;
          -webkit-user-select: none;
        }
        .bulk-create-sortable-helper td {
          background: #fff;
          vertical-align: middle;
        }
        .bulk-create-sortable-helper * {
          user-select: none;
          -webkit-user-select: none;
        }
        .bulk-create-drag-handle:active {
          cursor: grabbing;
        }
        body.bulk-create-sorting {
          user-select: none;
          -webkit-user-select: none;
        }
      `}</style>
      <div
        className="table-responsive border rounded bulk-create-manual-table"
        style={{ maxHeight: '420px', overflowX: 'auto', overflowY: 'auto' }}
      >
        <table className="table table-sm table-hover mb-0" style={{ minWidth: '1480px' }}>
          <thead className="thead-light">
            <tr>
              <th style={{ width: '36px' }} />
              <th style={{ width: '28px' }} />
              <th style={{ minWidth: '280px' }}>Назва</th>
              <th style={{ width: '220px' }}>Опис</th>
              <th style={{ minWidth: '130px' }}>Артикул</th>
              <th style={{ minWidth: '140px' }}>Штрихкод</th>
              <th style={{ minWidth: '260px' }}>Посилання</th>
              <th style={{ width: '72px' }}>К-сть</th>
              <th style={{ width: '88px' }}>{currencyOpt.label}</th>
              {showUahPreview ? <th style={{ width: '80px' }}>грн</th> : null}
              <th style={{ width: '140px' }}>Кат. зн.</th>
              <th style={{ width: '72px' }}>Зн.%</th>
              {categoryMode === 'each' ? <th style={{ width: '88px' }}>Кат.</th> : null}
              <th style={{ width: '36px' }} />
            </tr>
          </thead>
          {items.length ? (
            <SortableManualTableBody
              useDragHandle
              lockAxis="y"
              lockToContainerEdges
              distance={8}
              helperClass="bulk-create-sortable-helper"
              items={items}
              onSortStart={() => {
                document.body.classList.add('bulk-create-sorting');
              }}
              onSortEnd={(params) => {
                document.body.classList.remove('bulk-create-sorting');
                onSortEnd(params);
              }}
              disabled={loading}
              loading={loading}
              categoryMode={categoryMode}
              priceCurrency={priceCurrency}
              discountCategories={discountCategories}
              expandedCategoryRow={expandedCategoryRow}
              setExpandedCategoryRow={setExpandedCategoryRow}
              toggleItemSelected={toggleItemSelected}
              toggleItemCategory={toggleItemCategory}
              rowCategories={rowCategories}
              onChangeField={onChangeField}
              onPasteField={onPasteField}
              onChangeDiscount={onChangeDiscount}
              onRemoveRow={(rowIndex) => {
                const removed = items[rowIndex];
                if (removed && removed.key === descriptionEditKey) closeDescriptionEditor();
                onRemoveRow(rowIndex);
              }}
              onOpenDescriptionEditor={openDescriptionEditor}
            />
          ) : (
            <tbody>
              <tr>
                <td colSpan={colSpan} className="text-muted small text-center py-3">
                  Додайте рядок або вставте список у будь-яку колонку
                </td>
              </tr>
            </tbody>
          )}
        </table>
      </div>

      <BulkCreateDescriptionModal
        show={editingItem != null}
        value={editingItem ? editingItem.description : ''}
        productLabel={editingItem ? (editingItem.name || editingItem.productCode || '') : ''}
        loading={loading}
        onChange={(next) => {
          if (descriptionEditIndex < 0) return;
          onChangeField(descriptionEditIndex, 'description', next);
        }}
        onClose={closeDescriptionEditor}
      />
    </>
  );
};

export default BulkCreateManualTable;
